/* eslint-disable no-nested-ternary */
import React, { useState, useCallback } from 'react'
import { Formik, Form } from 'formik'
import CalculatorService from '../../services/calculator.service'
import { useTranslation } from 'react-i18next'
import { CALCULATOR_KEYS, AUTH_KEYS} from '../../utils/translation-constants'
import InputError from '../../components/InputError/InputError'
import Loading from 'custom/assets/loaders/Loading'
import NeoButton from '../../components/NeoButton/NeoButton'
import NeoInput from '../../components/NeoInput/NeoInput'
import useTabStyle from '../../hooks/useTabStyle'
import LayoutWithOutAccountCard from '../../layout/Layout/layout-without-card'
import { useHistory } from 'react-router-dom'
import { LoanSchema, DepositSchema } from '../../schemas/calculator'
import NumberFormat from 'react-number-format'

export default function Calculator() {
  const history = useHistory()
  const { t } = useTranslation()
  const [stage, setStage] = useState(0)
  const [isDeposit, setIsDeposit] = useState(false)
  const [result, setResult] = useState(null)
  const { classes } = useTabStyle()

  const calculateDepositResult = useCallback(
    async (data, { setSubmitting }) => {
      try {
      
        setSubmitting(true)
        setIsDeposit(true)
        const response = await CalculatorService.calculateDepositInterest({
          loanAmount: data.depositAmount,
          tenor: data.depositTenure,
          interestRate: data.depositInterestRate,
        })
        setResult(response)
        setSubmitting(false)
      } catch (err) {
      }
    },
    [],
  )

  const calculateLoanResult = useCallback(
    async (data, { setSubmitting }) => {
      try {
        setSubmitting(true)
        setIsDeposit(false)
        const response = await CalculatorService.calculateLoanEMI({
          loanAmount: data.loanAmount,
          tenor: data.loanTenure,
          interestRate: data.loanInterestRate,
        })
        setResult(response)
        setSubmitting(false)
      } catch (err) {
      }
    },
    [],
  )

  function renderLoading() {
    return (
      <div className='mx-4 rounded d-flex justify-content-center align-items-center'>
        <Loading width='50px' height='50px' />
      </div>
    )
  }

  function renderSwitch() {
    switch (stage) {
      case 0:
        return renderLoanCalculator()
      case 1:
        return renderDepositCalculator()
      case 2:
        return renderLoading()
      default:
        return renderLoading()
    }
  }

  function renderDepositCalculator() {
    return (
      <div>
        <Formik
          validationSchema={DepositSchema}
          initialValues={{
            depositAmount: '',
            depositTenure: '',
            depositInterestRate: '',
            depositAmountFormatted: '',
          }}
          enableReinitialize
          onSubmit={calculateDepositResult}>
          {({ isSubmitting, resetForm, errors, values, touched, handleChange, setFieldValue }) => (
            <Form>
              <div className='row'>
                <div className='form-group col-12'>
                  <NeoInput
                    text={t(CALCULATOR_KEYS.DEPOSITE)}
                    onChange={(values) => {
                      setFieldValue('depositAmount', values.value)
                    }}
                    name='depositAmountFormatted'
                    mode='amount'
                    value={values.depositAmountFormatted}
                  />
                  <InputError text={errors.depositAmount} touched={touched.depositAmount} />
                </div>
              </div>
              <div className='row'>
                <div className='form-group col-12'>
                  <NeoInput
                    text={t(CALCULATOR_KEYS.TENURE)}
                    onChange={handleChange}
                    type='number'
                    mode='onlyNumber'
                    onChange={(value) => {
                      setFieldValue('depositTenure', value?.value)
                    }}
                    name='depositTenure'
                    value={values.depositTenure}
                    maxLength={3}
                  />
                  <InputError text={errors.depositTenure} touched={touched.depositTenure} />
                </div>
              </div>
              <div className='row'>
                <div className='form-group col-12'>
                  <NeoInput
                    text={t(CALCULATOR_KEYS.INTEREST_RATE)}
                    onChange={handleChange}
                    type='number'
                    mode='onlyNumber'
                    onChange={(value) => {
                      setFieldValue('depositInterestRate', value?.value)
                    }}
                    name='depositInterestRate'
                    value={values.depositInterestRate}
                    maxLength={3}
                  />
                  <InputError
                    text={errors.depositInterestRate}
                    touched={touched.depositInterestRate}
                  />
                </div>
              </div>
              <div className='mt-3'>
                {result !== null ? (
                  <NeoButton
                    type='button'
                    text={t(CALCULATOR_KEYS.CLEAR)}
                    mode='newDesign'
                    onClick={() => {
                      setResult(null)
                      resetForm()
                    }}
                  />
                ) : null}
                {result === null ? (
                  isSubmitting ? (
                    renderLoading()
                  ) : (
                    <NeoButton
                      type='submit'
                      mode='newDesign'
                      text={t(CALCULATOR_KEYS.CALCULATE)}   
                      disabled={isSubmitting}
                    />
                  )
                ) : null}
              </div>
            </Form>
          )}
        </Formik>
      </div>
    )
  }

  function renderLoanCalculator() {
    return (
      <div>
        <Formik
          initialValues={{
            loanAmount: '',
            loanTenure: '',
            loanInterestRate: '',
            loanAmountFormatted: '',
          }}
          validationSchema={LoanSchema}
          enableReinitialize
          onSubmit={calculateLoanResult}>
          {({ isSubmitting, resetForm, errors, touched, values, handleChange, setFieldValue }) => (
            <Form>
              <div className='row'>
                <div className='form-group col-12'>
                  <NeoInput
                    text={t(CALCULATOR_KEYS.LOAN)}
                    onChange={(values) => {
                      setFieldValue('loanAmount', values.value)
                    }}
                    mode='amount'
                    name='loanAmountFormatted'
                    value={values.loanAmountFormatted}
                  />
                  <InputError text={errors.loanAmount} touched={touched.loanAmount} />
                </div>
              </div>
              <div className='row'>
                <div className='form-group col-12'>
                  <NeoInput
                    text={t(CALCULATOR_KEYS.TENURE)}
                    onChange={handleChange}
                    type='number'
                    onChange={(value) => {
                      setFieldValue('loanTenure', value?.value)
                    }}
                    name='loanTenure'
                    mode='onlyNumber'
                    value={values.loanTenure}
                    maxLength={3}
                  />
                  <InputError text={errors.loanTenure} touched={touched.loanTenure} />
                </div>
              </div>
              <div className='row'>
                <div className='form-group col-12'>
                  <NeoInput
                    text={t(CALCULATOR_KEYS.INTEREST_RATE)}
                    onChange={handleChange}
                    type='number'
                    mode='onlyNumber'
                    onChange={(value) => {
                      setFieldValue('loanInterestRate', value?.value)
                    }}
                    name='loanInterestRate'
                    value={values.loanInterestRate}
                    maxLength={3}
                  />
                  <InputError text={errors.loanInterestRate} touched={touched.loanInterestRate} />
                </div>
              </div>
              <div className='mt-3'>
                {result !== null ? (
                  <NeoButton
                    type='button'
                    text={t(CALCULATOR_KEYS.CLEAR)}
                    mode='newDesign'
                    onClick={() => {
                      setResult(null)
                      resetForm()
                    }}
                  />
                ) : null}
                {result === null ? (
                  isSubmitting ? (
                    renderLoading()
                  ) : (
                    <NeoButton
                      type='submit'
                      mode='newDesign'
                      text={t(CALCULATOR_KEYS.CALCULATE)}   
                      disabled={isSubmitting}
                    />
                  )
                ) : null}
              </div>
            </Form>
          )}
        </Formik>
      </div>
    )
  }

  return (
    <LayoutWithOutAccountCard breadCumKey='CALCULATOR' showEmptySpace={true} keyPath={'CALCULATOR'}>
      <div className=''>
        <div className='row tab-navbar'>
          <div className={stage === 0 ? 'col-4 selected' : 'col-4'}>
            <span
              onClick={() => {
                setStage(0)
                setResult(null)
              }}>
              {t(CALCULATOR_KEYS.LOANS_HEADER)}
            </span>
          </div>
          <div className={stage === 1 ? 'col-4 selected' : 'col-4'}>
            <span
              onClick={() => {
                setStage(1)
                setResult(null)
              }}>
              {t(CALCULATOR_KEYS.DEPOSITS_HEADER)}
            </span>
          </div>
        </div>

        <div className='new-design-form-style'>
          {renderSwitch()}

          {result !== null && (
            <div className='my-5 py-3 px-5 rounded-1' style={{ backgroundColor: '#F15E2A' }}>
              <div className='row my-3 text-white'>
                {isDeposit == true ? (
                  <div className='col-6'>{t(CALCULATOR_KEYS.INTREST_AMOUNT)}</div>
                ) : (
                  <div className='col-6'>{t(CALCULATOR_KEYS.MONTHLY_INSTALLMENT)}</div>
                )}
                <div className='col-6 font-weight-bold text-right'>
                  <NumberFormat
                    value={Math.abs(result.emiAmount).toFixed(2)}
                    displayType={'text'}
                    thousandSeparator={true}
                    prefix={''}
                  />
                </div>
              </div>
              <div className='row my-3 text-white'>
                {isDeposit == true ? (
                  <div className='col-6'>{t(CALCULATOR_KEYS.MATURITY_AMOUNT)}</div>
                ) : (
                  <div className='col-6'>{t(CALCULATOR_KEYS.TOTAL_AMOUNT)}</div>
                )}
                <div className='col-6 font-weight-bold text-right'>
                  <NumberFormat
                    value={Math.abs(result.totalAmount).toFixed(2)}
                    displayType={'text'}
                    thousandSeparator={true}
                    prefix={''}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </LayoutWithOutAccountCard>
  )
}
